import { useEffect } from 'react';
import { useStoreConfig } from '../hooks/useStoreConfig';

interface Props {
  slug: string;
  status: 'success' | 'cancel';
}

/** Read a payment reference the provider may append to the return URL. */
function readReference(): string | null {
  if (typeof window === 'undefined') return null;
  const p = new URLSearchParams(window.location.search);
  return (
    p.get('reference') ??
    p.get('trxref') ??
    p.get('session_id') ??
    p.get('transaction_id') ??
    null
  );
}

const COPY = {
  success: {
    emoji: '✓',
    title: 'Payment received',
    body: "Thanks for your order! We're confirming your payment now and will email your receipt shortly. You can head back to the chat to keep shopping.",
    cta: 'Back to the store',
  },
  cancel: {
    emoji: '↩',
    title: 'Checkout cancelled',
    body: "No charge was made. Your cart is saved — head back to the chat whenever you're ready to finish.",
    cta: 'Return to your cart',
  },
} as const;

export default function OrderStatusPage({ slug, status }: Props) {
  const { config } = useStoreConfig(slug);
  const copy = COPY[status];
  const reference = readReference();
  const success = status === 'success';

  // Apply store brand colors when the config arrives.
  useEffect(() => {
    const c = config?.brandColors;
    if (c?.accent) document.documentElement.style.setProperty('--brand-accent', c.accent);
    if (c?.primary) document.documentElement.style.setProperty('--brand-primary', c.primary);
  }, [config]);

  return (
    <div className="flex min-h-[100dvh] w-full items-center justify-center bg-surface p-6">
      <div className="w-full max-w-[420px] rounded-md bg-white p-8 text-center shadow-card">
        {config?.logoUrl ? (
          <img
            src={config.logoUrl}
            alt=""
            className="mx-auto mb-5 h-12 w-12 rounded-full object-cover"
          />
        ) : null}

        <div
          className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full text-2xl font-bold text-white"
          style={{ backgroundColor: success ? '#22C55E' : 'var(--brand-accent)' }}
        >
          {copy.emoji}
        </div>

        <h1 className="text-lg font-bold text-primary">{copy.title}</h1>
        <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-mid">{copy.body}</p>

        {reference ? (
          <p className="mt-4 break-all text-[11px] text-mid">
            Reference: <span className="font-mono text-primary">{reference}</span>
          </p>
        ) : null}

        <a
          href={`/${encodeURIComponent(slug)}`}
          className="mt-6 inline-block w-full rounded-sm px-4 py-3 text-sm font-semibold text-white transition-transform active:scale-[0.98]"
          style={{ backgroundColor: 'var(--brand-accent)' }}
        >
          {copy.cta}
        </a>

        {config?.name ? (
          <p className="mt-4 text-[11px] text-mid">{config.name}</p>
        ) : null}
      </div>
    </div>
  );
}
